import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import moment from 'moment';
import {AttendanceTrackerSessions} from '../../backend/responses/attendance-tracker-session';
import {useCanTrackAttendance} from '../../hooks/use-can-track-attendance';
import {EntityAttendanceTrackerRecordsRepository} from '../../repositories/entity-attendance-tracker-records';
import {useAuth} from '../auth/context';
import {AttendanceTrackerStatusContext} from './context';

type AttendanceTrackerStatusProviderProps = {
  children: ReactNode;
};

/**
 * Keeps the attendance status of the logged user (if there is an open session) for the whole Daily Care Log,
 * so the tab, the ongoing bar and the screens do not need to ask for it on their own.
 * It is obtained again when the user navigates, when the app is shown again and when asked to refresh.
 */
export const AttendanceTrackerStatusProvider = ({
  children,
}: AttendanceTrackerStatusProviderProps) => {
  const {state} = useAuth();
  const canTrackAttendance = useCanTrackAttendance();
  const {pathname} = useLocation();
  const entityId = state.session?.selectedAffiliation?.entity.id;

  const [data, setData] = useState<AttendanceTrackerSessions | undefined>(undefined);

  const refresh = useCallback(async () => {
    if (!canTrackAttendance || !entityId) {
      setData(undefined);
      return;
    }

    try {
      // The open session is always returned, whatever the interval
      setData(
        await EntityAttendanceTrackerRecordsRepository.instance.getMySessions(
          entityId,
          moment().startOf('day').toISOString(),
          moment().endOf('day').toISOString()
        )
      );
    } catch (e) {
      console.error(e);
      setData(undefined);
    }
  }, [canTrackAttendance, entityId]);

  // Another entity or another screen
  useEffect(() => {
    refresh();
  }, [refresh, pathname]);

  // The app was in the background (e.g. an exit was registered in another device)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refresh]);

  const value = useMemo(
    () => ({
      isLoaded: !!data,
      openSession: data?.openSession,
      serverTime: data?.serverTime,
      refresh,
    }),
    [data, refresh]
  );

  return (
    <AttendanceTrackerStatusContext.Provider value={value}>
      {children}
    </AttendanceTrackerStatusContext.Provider>
  );
};
